import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Storefront, ArrowLeft } from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COUNTRIES, dialByCountry } from "@/lib/countries";
import PhoneInput from "@/components/PhoneInput";
import CitySelect from "@/components/CitySelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

export default function BusinessRegister() {
  const { applyAuth } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [catSearch, setCatSearch] = useState("");
  const [f, setF] = useState({
    business_name: "", trading_name: "", category: "", country: "Tanzania",
    city: "", phone: "+255", email: "", owner_name: "", owner_email: "", owner_password: "", main_branch: "Main Branch",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    api.get("/config/categories").then((r) => {
      setCategories(r.data);
      setF((prev) => ({ ...prev, category: prev.category || r.data[0] || "" }));
    }).catch(() => {});
  }, []);

  const submit = async () => {
    if (!f.business_name.trim()) return toast.error("Enter your business name");
    if (!f.category) return toast.error("Select a category");
    if (!f.city) return toast.error("Please select a city");
    if (!f.owner_name.trim() || !f.owner_email.trim() || !f.owner_password) return toast.error("Complete the owner account details");
    setLoading(true);
    try {
      const { data } = await api.post("/businesses/register", f);
      applyAuth(data);
      toast.success("Business submitted — pending OneKite approval");
      nav("/business");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const field = (label, key, props = {}) => (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input data-testid={`biz-${key}`} value={f[key]} onChange={set(key)} className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" {...props} />
    </div>
  );

  return (
    <div className="min-h-screen ok-bg grain relative flex items-center justify-center px-5 py-10">
      <Link to="/" data-testid="back-home" className="absolute top-5 left-5 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft size={18} /> Home
      </Link>
      <div className="w-full max-w-2xl">
        <Link to="/" className="flex items-center gap-2 justify-center mb-6">
          <Logo className="h-14" />
        </Link>
        <div className="card-surface p-7 fade-up">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-11 w-11 rounded-xl bg-primary/15 flex items-center justify-center">
              <Storefront size={22} weight="duotone" className="text-primary" />
            </div>
            <div>
              <h1 className="font-display font-bold text-2xl">Register your business</h1>
              <p className="text-sm text-muted-foreground">Your account starts pending until OneKite approves it.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field("Business name", "business_name")}
            {field("Trading name (optional)", "trading_name")}
            <div>
              <Label className="text-xs text-muted-foreground">Category</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })} onOpenChange={(o) => !o && setCatSearch("")}>
                <SelectTrigger data-testid="biz-category" className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  <div className="p-2 sticky top-0 bg-popover z-10 border-b border-black/5">
                    <Input
                      data-testid="biz-category-search"
                      value={catSearch}
                      onChange={(e) => setCatSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      placeholder="Search categories…"
                      className="h-9 bg-muted/40 border-black/10 rounded-lg text-sm"
                    />
                  </div>
                  {categories.filter((c) => c.toLowerCase().includes(catSearch.trim().toLowerCase())).map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                  {categories.filter((c) => c.toLowerCase().includes(catSearch.trim().toLowerCase())).length === 0 && (
                    <p className="px-2 py-3 text-sm text-muted-foreground text-center" data-testid="biz-category-empty">No categories found</p>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Country</Label>
              <Select value={f.country} onValueChange={(v) => setF({ ...f, country: v, phone: dialByCountry(v) })}>
                <SelectTrigger data-testid="biz-country" className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => <SelectItem key={c.code} value={c.name}><span className="flex items-center gap-2"><span className="text-base leading-none">{c.flag}</span> {c.name}</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">City</Label>
              <CitySelect testid="biz-city" value={f.city} onChange={(v) => setF({ ...f, city: v })} country={f.country} />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Business phone</Label>
              <div className="mt-1.5"><PhoneInput testid="biz-phone" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} /></div>
            </div>
            {field("Business email", "email", { type: "email" })}
            {field("Main branch name", "main_branch")}
          </div>

          <div className="border-t border-black/10 my-6" />
          <h2 className="font-display font-semibold text-lg mb-4">Owner account</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field("Owner full name", "owner_name")}
            {field("Owner login email", "owner_email", { type: "email" })}
            {field("Owner password", "owner_password", { type: "password" })}
          </div>

          <Button data-testid="biz-register-submit" onClick={submit} disabled={loading} className="w-full h-11 mt-6 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
            {loading ? "Submitting…" : "Create business"}
          </Button>
          <p className="text-sm text-center text-muted-foreground mt-4">
            Already registered? <Link to="/login" className="text-primary font-medium">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
